/*
  Glimmery cart drawer ("Your Cart"): modal dialog with focus trap, Escape, focus return and a scroll lock
  that every close path releases. While it is open the rest of the page (header, main, footer: the drawer's
  siblings in <body>) is inert, like the menu drawer, so screen reader swipe navigation stays in the bag too
  (aria-modal alone is not honored everywhere). Line changes run through window.GlimmeryCart (assets/cart.js).
*/
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    static NOTICE_MS = 4000;

    constructor() {
      super();
      this.isOpen = false;
      this.opener = null;
      this.inerted = [];
      this.onClick = this.onClick.bind(this);
      this.onFieldChange = this.onFieldChange.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onFieldChange);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      if (typeof subscribe === 'function' && !this.unsubscribe) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
      }
      this.setHeaderCartIconAccessibility();
      if (window.GlimmeryCart) this.setupLines();
      else document.addEventListener('DOMContentLoaded', () => this.setupLines(), { once: true });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onFieldChange);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('focusin', this.onFocusIn);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.lines?.destroy();
      this.lines = null;
      this.unlockScroll();
      this.releaseInert();
    }

    setupLines() {
      if (this.lines || !window.GlimmeryCart || !this.isConnected) return;
      this.lines = window.GlimmeryCart.createLines(this, {
        source: 'cart-drawer-items',
        sectionIds: () => ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'],
        render: (sections) => this.renderSections(sections),
        liveRegion: () => this.querySelector('#CartDrawer-LiveRegionText'),
        focusFallback: () => (this.isOpen ? this.dialog : null),
      });
    }

    get dialog() {
      return this.querySelector('#CartDrawer-Dialog') || this.querySelector('.drawer__inner');
    }

    isCartPage() {
      return Boolean(document.querySelector('cart-items'));
    }

    // The header bag link opens the drawer (it stays a plain link on /cart and without JS).
    setHeaderCartIconAccessibility() {
      const link = document.getElementById('cart-icon-bubble');
      if (!link || this.isCartPage()) return;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
    }

    onSectionLoad() {
      this.setHeaderCartIconAccessibility();
    }

    onDocumentClick(event) {
      const link = event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!link || this.isCartPage()) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      this.open(link);
    }

    onClick(event) {
      // A new bag change starts: the previous "Added" notice no longer describes the bag.
      if (event.target.closest('.quantity__button, [data-g-remove], [data-g-upsell-add]')) this.hideNotice();
      if (!event.target.closest('[data-cart-drawer-close]')) return;
      event.preventDefault();
      this.close();
    }

    onFieldChange(event) {
      if (event.target.classList && event.target.classList.contains('quantity__input')) this.hideNotice();
    }

    onDocumentKeydown(event) {
      if (event.key === ' ' && event.target.closest && event.target.closest('#cart-icon-bubble') && !this.isCartPage()) {
        event.preventDefault();
        this.open(event.target.closest('#cart-icon-bubble'));
        return;
      }
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        if (document.querySelector('cart-disclosure-modal[open]')) return;
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapTab(event);
    }

    trapTab(event) {
      const dialog = this.dialog;
      const items = this.getFocusable();
      if (!items.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const atStart = active === first || active === dialog || (active && active.id === 'CartDrawer-Heading');
      if (event.shiftKey && (atStart || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusable() {
      const selector =
        'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';
      return Array.from(this.dialog.querySelectorAll(selector)).filter(
        (element) =>
          !element.disabled &&
          element.getAttribute('tabindex') !== '-1' &&
          !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
          element.getClientRects().length > 0
      );
    }

    // Keep focus inside the open dialog (cart disclosure modals sit on top and are allowed).
    onFocusIn(event) {
      if (!this.isOpen) return;
      const dialog = this.dialog;
      if (!dialog || dialog.contains(event.target)) return;
      if (event.target.closest && event.target.closest('cart-disclosure-modal')) return;
      dialog.focus();
    }

    // options.focusHeading: after an add, focus lands on the "Your Cart (N)" heading instead of the dialog.
    open(opener, options = {}) {
      if (opener) this.opener = opener;
      else if (!this.opener && document.activeElement !== document.body) this.opener = document.activeElement;

      const dialog = this.dialog;
      if (this.isOpen) {
        if (options.focusHeading) this.focusHeading();
        else dialog?.focus();
        return;
      }
      this.isOpen = true;
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.lockScroll();
      this.setOutsideInert();
      this.classList.add('animate', 'active');
      document.addEventListener('focusin', this.onFocusIn);
      window.requestAnimationFrame(() => {
        if (!this.isOpen) return;
        if (options.focusHeading) this.focusHeading();
        else dialog?.focus({ preventScroll: true });
      });
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
    }

    close() {
      const wasOpen = this.isOpen;
      this.isOpen = false;
      this.classList.remove('active');
      document.removeEventListener('focusin', this.onFocusIn);
      this.unlockScroll();
      this.releaseInert();
      this.showNotice('');
      if (!wasOpen) return;

      const opener = this.opener;
      this.opener = null;
      const target =
        opener && opener.isConnected && opener.getClientRects().length > 0
          ? opener
          : document.getElementById('cart-icon-bubble');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      target?.focus();
    }

    lockScroll() {
      document.documentElement.classList.add('g-scroll-lock');
    }

    unlockScroll() {
      document.documentElement.classList.remove('g-scroll-lock');
    }

    // Makes everything beside the drawer inert (from the drawer up to <body>). Elements that were inert already
    // (e.g. set by another component) are left alone and are not released later.
    setOutsideInert() {
      this.releaseInert();
      let node = this;
      while (node && node !== document.body && node.parentElement) {
        const parent = node.parentElement;
        for (const sibling of parent.children) {
          if (sibling === node || sibling.inert || sibling.matches('script, style, link, template, noscript')) continue;
          sibling.inert = true;
          this.inerted.push(sibling);
        }
        node = parent;
      }
    }

    releaseInert() {
      if (!this.inerted || !this.inerted.length) return;
      this.inerted.forEach((element) => {
        element.inert = false;
      });
      this.inerted = [];
    }

    /*
      Visual "Added to your bag" notice (aria-hidden; screen readers get one combined message in the live region).
      Transient: it clears after NOTICE_MS, on the next bag change (any re-render, a line control) and on close,
      so it never describes an older state or an empty bag.
    */
    showNotice(text) {
      const notice = this.querySelector('#CartDrawer-Notice');
      if (!notice) return;
      window.clearTimeout(this.noticeTimer);
      window.clearTimeout(this.noticeHideTimer);
      notice.classList.remove('is-leaving');
      notice.textContent = '';
      if (!text) return;
      this.noticeTimer = window.setTimeout(() => {
        notice.textContent = text;
        this.noticeHideTimer = window.setTimeout(() => this.hideNotice(), CartDrawer.NOTICE_MS);
      }, 60);
    }

    // Fades the notice out (CSS .is-leaving), then empties it (:empty hides it).
    hideNotice() {
      const notice = this.querySelector('#CartDrawer-Notice');
      window.clearTimeout(this.noticeTimer);
      if (!notice || !notice.textContent) return;
      window.clearTimeout(this.noticeHideTimer);
      notice.classList.add('is-leaving');
      this.noticeHideTimer = window.setTimeout(() => {
        notice.classList.remove('is-leaving');
        notice.textContent = '';
      }, 200);
    }

    // Replaces heading + body from a "cart-drawer" section render; returns the live region text.
    renderSections(sections) {
      // Every re-render is a newer bag state than the one an "Added" notice described.
      this.showNotice('');
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const heading = doc.getElementById('CartDrawer-Heading');
      const body = doc.getElementById('CartDrawer-Body');
      const targetHeading = this.querySelector('#CartDrawer-Heading');
      const targetBody = this.querySelector('#CartDrawer-Body');
      if (!heading || !body || !targetHeading || !targetBody) throw new Error('Cart drawer markup missing');

      const count = parseInt(body.dataset.itemCount || '0', 10);
      targetHeading.innerHTML = heading.innerHTML;
      targetBody.innerHTML = body.innerHTML;
      targetBody.dataset.itemCount = count;
      this.classList.toggle('is-empty', count === 0);

      const cart = window.GlimmeryCart;
      if (cart) {
        cart.renderBubble(sections['cart-icon-bubble']);
        return cart.sectionText(sections['cart-live-region-text']);
      }
      return '';
    }

    // Called by product-form after a successful add. Throws when the response cannot be rendered,
    // so product-form can fall back to its in-place confirmation (the item is in the bag either way).
    renderContents(parsedState) {
      const sections = parsedState && parsedState.sections;
      if (!sections || typeof sections['cart-drawer'] !== 'string') throw new Error('Cart drawer section missing');
      const snapshotOpen = this.isOpen;
      const liveText = this.renderSections(sections);
      if (window.GlimmeryCart && typeof sections['cart-icon-bubble'] === 'string') {
        window.GlimmeryCart.renderBubble(sections['cart-icon-bubble']);
      }
      // Anything rendered before this add is older than what is on screen now.
      if (this.lines && window.GlimmeryCart) this.lines.renderedSeq = window.GlimmeryCart.nextSeq();
      this.open(null, { focusHeading: true });
      const line = this.findAddedLine(parsedState);
      const added = this.dataset.addedText || '';
      this.showNotice(added);
      this.highlightLine(line);
      // One announcement for the add:
      // "Added to cart: All/In Graphic T-Shirt, Front print, size M. Cart updated: 4 items, subtotal $114.13."
      const lineLabel = line && line.dataset.gAddedLabel;
      this.announceAdd(lineLabel || added, liveText);
      if (snapshotOpen) this.focusHeading();
    }

    // The line the add response describes (line item key; variant id as a fallback for responses without a key).
    findAddedLine(state) {
      if (!state) return null;
      const lines = Array.from(this.querySelectorAll('.g-cart-line[data-line-key]'));
      const key = state.key || (state.items && state.items[0] && state.items[0].key);
      let line = key ? lines.find((element) => element.dataset.lineKey === String(key)) : null;
      const variant = state.variant_id || state.id || (state.items && state.items[0] && state.items[0].variant_id);
      if (!line && variant) line = lines.find((element) => element.dataset.variantId === String(variant));
      return line || null;
    }

    // Brief, quiet highlight of the added line (a tinted row, not a flashing checkmark). Cleared by any re-render.
    highlightLine(line) {
      if (!line) return;
      line.classList.add('is-added');
      window.clearTimeout(this.highlightTimer);
      this.highlightTimer = window.setTimeout(() => line.classList.remove('is-added'), 2400);
      if (typeof line.scrollIntoView === 'function') {
        window.requestAnimationFrame(() => line.scrollIntoView({ block: 'nearest' }));
      }
    }

    focusHeading() {
      const heading = this.querySelector('#CartDrawer-Heading');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      } else {
        this.dialog?.focus({ preventScroll: true });
      }
    }

    announceAdd(added, liveText) {
      const region = this.querySelector('#CartDrawer-LiveRegionText');
      const head = added && !/[.!?]$/.test(added) ? `${added}.` : added;
      const text = [head, liveText].filter(Boolean).join(' ');
      if (window.GlimmeryCart) window.GlimmeryCart.announce(region, text);
      else if (region) region.textContent = text;
    }

    onCartUpdate(event) {
      if (!event || event.source === 'cart-drawer-items' || event.source === 'product-form') return;
      // On /cart the drawer never opens; pageshow refreshes it if the visitor comes back elsewhere.
      if (this.isCartPage() && !this.isOpen) return;
      if (!this.lines) this.setupLines();
      return this.lines?.refresh();
    }

    // Back/forward cache: the page may show an old bag; refresh it and the count.
    onPageShow(event) {
      if (!event.persisted) return;
      if (!this.lines) this.setupLines();
      this.lines?.refresh();
      if (!this.isOpen) {
        this.unlockScroll();
        this.releaseInert();
      }
    }

    // product-form requests these sections with an add; cart-live-region-text feeds the add announcement.
    // No selector on the last entry: standard-actions-override.js would otherwise resolve it against the
    // whole document (it only mounts entries whose id exists in the page).
    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' },
        { id: 'cart-live-region-text' },
      ];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    setActiveElement(element) {
      this.opener = element && element !== document.body ? element : null;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
